// Ember — core domain types. Human pace, not productivity.

export type Season = 
  | 'deep_winter'   // Nov–Feb: rest, story, repair, the long dark
  | 'thaw'          // Mar–Apr: mud, emergence, unreliable
  | 'planting'      // May: school wind-down + ground work
  | 'high_summer'   // Jun–Aug: visitors, labor, festivals, heat
  | 'harvest'       // Sep–Oct: fair, abundance, exhaustion
  | 'back_to_dark'  // late Oct: the turn

export const SEASONS: Season[] = ['deep_winter', 'thaw', 'planting', 'high_summer', 'harvest', 'back_to_dark']

export const SEASON_LABEL: Record<Season, string> = {
  deep_winter: 'Deep Winter',
  thaw: 'Thaw & Mud',
  planting: 'Planting & Windup',
  high_summer: 'High Summer',
  harvest: 'Harvest & Fair',
  back_to_dark: 'Back to the Dark',
}

export const SEASON_HINT: Record<Season, string> = {
  deep_winter: 'When the town mostly talks to itself. Grief and warmth live here.',
  thaw: 'Everything is possible and nothing is reliable. People are tired of waiting.',
  planting: 'School, gardens, and the first big push. Overlap is invisible until it breaks someone.',
  high_summer: 'The season outsiders see. Locals carry the performance + the heat.',
  harvest: 'Abundance that still requires more labor than exists. The fair is beautiful and brutal.',
  back_to_dark: 'The moment the calendar turns and everyone feels the old dread or the old relief.',
}

export type ScarcityKind = 'people' | 'time' | 'goodwill' | 'money' | 'certainty' | 'energy'

export const SCARCITY_LABEL: Record<ScarcityKind, string> = {
  people: 'Not enough people',
  time: 'Not enough hours in a day',
  goodwill: 'Goodwill is running out',
  money: 'Not enough money',
  certainty: 'We no longer know what works',
  energy: 'The people who carry it are exhausted',
}

export type TradeoffDomain = 
  | 'spontaneous_gatherings'
  | 'youth_presence'
  | 'elder_participation'
  | 'unpaid_care'
  | 'winter_rest'
  | 'main_street_daily_life'
  | 'the_ones_who_stay'

export const TRADEOFF_LABEL: Record<TradeoffDomain, string> = {
  spontaneous_gatherings: 'Spontaneous, unscheduled time together',
  youth_presence: 'Young people simply being around',
  elder_participation: 'Elders in the room without a job to do',
  unpaid_care: 'The invisible work that keeps people alive here',
  winter_rest: 'Winter as a time of being, not performing',
  main_street_daily_life: 'The slow daily texture of the street',
  the_ones_who_stay: 'The quiet ones who never leave and rarely complain',
}

export type UrgencySource = 
  | 'genuine_crisis'
  | 'grant_deadline'
  | 'vocal_complaint'
  | 'habit_performance'
  | 'fear_of_appearing_idle'
  | 'other'

export const URGENCY_LABEL: Record<UrgencySource, string> = {
  genuine_crisis: 'Genuine safety or survival pressure',
  grant_deadline: 'A grant, report, or external clock',
  vocal_complaint: 'A loud voice (or several) saying we must act now',
  habit_performance: 'We have always moved fast when something feels broken',
  fear_of_appearing_idle: 'If we slow down, someone will say we do not care',
  other: 'Something else I cannot yet name',
}

export type Circle = {
  id: string
  code: string
  name: string
  keeperHash: string
  createdAt: string
}

export type Card = {
  id: string
  circleId: string
  tensionTitle: string
  tensionBody: string
  pressureSeasons: Season[]
  scarcityKinds: ScarcityKind[]
  scarcityIntensity: number
  tradeoffs: (TradeoffDomain | string)[]
  urgencySource: UrgencySource
  urgencyNote?: string
  clarifyingAnger: string
  paceBoundary: string
  boundaryStrength: number
  reliefScore: number
  reframedStatement: string
  almanacNote?: string
  isPublished: boolean
  createdAt: string
}

export type SignalEvent = {
  id: string
  circleId: string
  cardId?: string
  signalType: 'pressure' | 'scarcity' | 'tradeoff' | 'urgency' | 'anger' | 'boundary' | 'relief'
  payload: Record<string, unknown>
  createdAt: string
}

export type LiveSessionState = {
  id: string
  code: string
  circleId: string
  step: number
  pressureSeasons: Season[]
  scarcityKinds: ScarcityKind[]
  scarcityIntensity: number
  tradeoffs: (TradeoffDomain | string)[]
  urgencySource?: UrgencySource
  urgencyNote?: string
  clarifyingAnger?: string
  paceBoundary?: string
  boundaryStrength?: number
  reliefScore?: number
  contributors: string[] // display names who have added something
}

export type ReframeReturn = {
  title: string
  body: string
  // A short "what this protects" or "what you just named"
  insight?: string
}
